import React from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "./useAuth";
import { redirectToAuth } from "@/lib/auth-redirect";

const DEV_AUTH_BYPASS =
  import.meta.env.DEV && import.meta.env.VITE_AUTH_BYPASS === "true";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (DEV_AUTH_BYPASS) {
    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-900">
        <div className="flex flex-col items-center gap-4 animate-fade-in">
          <div className="h-10 w-10 rounded-xl bg-blue-600 flex items-center justify-center">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a10 10 0 1 0 10 10" />
            </svg>
          </div>
          <p className="text-sm text-slate-400">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // H8: leave cross-origin directly — no intermediate `/auth` SPA hop.
    // H4: portal-scoped sessionStorage key persists the deep link.
    // H3: redirects to /login (not the bare auth origin).
    // H5: do NOT set document.title here.
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    const safe =
      returnTo.startsWith("/") && !returnTo.startsWith("/callback")
        ? returnTo
        : "/";
    try {
      redirectToAuth(safe);
    } catch {
      /* redirectToAuth throws "unreachable" by contract — swallow it so React
         can render the brief brand frame in the synchronous gap before the
         browser tears down this document. */
    }
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-900">
        <p className="text-sm text-slate-400">Signing you in&hellip;</p>
      </div>
    );
  }

  return <>{children}</>;
}
